import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import Pusher from "pusher-js";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import moment from "moment";
import { useLocalStorage } from "../../auth/useLocalStorage";
import { posToken } from "../../services/AjaxService";
import { DeliveryBoardService } from "../../services/DeliveryBoardService";
import { IDeliveryBoard, IDeliveryBoardItem, IDeliveryDriver } from "../../common/interfaces";
import { DeliveryCard } from "./deliveryCard";
import { DeliveryMap } from "./deliveryMap";
import { DeliverySettlement } from "./deliverySettlement";
import { money } from "./deliveryFormat";

const MySwal = withReactContent(Swal);
const POLL_MS = 10000;

const COLUMNS: { key: string; title: string; statuses: string[] }[] = [
  { key: "pool", title: "Bolsa", statuses: ["pending"] },
  { key: "assigned", title: "Asignados", statuses: ["assigned"] },
  { key: "route", title: "En ruta", statuses: ["in_route"] },
  { key: "failed", title: "Incidencias", statuses: ["failed"] },
];

interface Props {
  focusVentaId?: number;
}

// Tablero de domicilios del POS: Bolsa · Asignados · En ruta · Incidencias, mapa de
// repartidores en turno, asignar/reasignar, resolver incidencias y entregado manual.
// Se actualiza por soketi (canal privado de la sucursal) y cada 10 s por si acaso.
export const DeliveryBoard: React.FC<Props> = ({ focusVentaId }) => {
  const [user] = useLocalStorage("user", null);
  const warehouseId: number | undefined = user?.warehouse_id ?? user?.cbox?.warehouse_id;
  const [board, setBoard] = useState<IDeliveryBoard | null>(null);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string>("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [assigning, setAssigning] = useState<IDeliveryBoardItem | null>(null);
  const [view, setView] = useState<"open" | "closed" | "settlement">("open");
  const loadingRef = useRef(false);

  const load = useCallback(async () => {
    if (!warehouseId || loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const res = await DeliveryBoardService.getBoard(warehouseId);
      if (res.success && res.object) {
        setBoard(res.object);
        setError("");
        setUpdatedAt(moment().format("HH:mm:ss"));
      } else if (res.status === 401) {
        setError("Tu sesión del POS no es válida para el tablero. Cierra sesión y vuelve a entrar.");
      } else if (res.status === 403) {
        setError("Tu usuario no tiene permiso para el tablero de domicilios. Pide que tu rol tenga el permiso \"Tablero de domicilios: Ver\".");
      } else {
        setError(res.message || "No se pudo cargar el tablero.");
      }
    } catch (e) {
      setError("No hay conexión con el servidor.");
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [warehouseId]);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  // Tiempo real por soketi (si el back lo tiene configurado).
  const rt = board?.realtime;
  useEffect(() => {
    if (!rt?.key || !rt?.host) return;
    const client = new Pusher(rt.key, {
      wsHost: rt.host,
      wsPort: rt.port,
      wssPort: rt.port,
      forceTLS: rt.tls,
      enabledTransports: ["ws", "wss"],
      cluster: "mt1",
      disableStats: true,
      channelAuthorization: {
        endpoint: DeliveryBoardService.broadcastAuthEndpoint,
        transport: "ajax",
        headers: { Authorization: `Bearer ${posToken()}`, Accept: "application/json" },
      },
    });
    const channel = client.subscribe(rt.channel);
    channel.bind("deliveries.changed", () => load());
    return () => {
      channel.unbind_all();
      client.unsubscribe(rt.channel);
      client.disconnect();
    };
  }, [rt?.key, rt?.host, rt?.channel, load]);

  // Al abrir desde "Pedidos Clientes" se selecciona ese pedido.
  useEffect(() => {
    if (!focusVentaId || !board) return;
    const d = board.deliveries.find((x) => x.venta_id === focusVentaId);
    if (d) setSelectedId(d.id);
  }, [focusVentaId, board]);

  const open = useMemo(() => (board?.deliveries || []).filter((d) => !["delivered", "cancelled"].includes(d.status)), [board]);
  const closed = useMemo(() => (board?.deliveries || []).filter((d) => ["delivered", "cancelled"].includes(d.status)), [board]);

  const run = async (promise: Promise<any>, okText: string) => {
    const res = await promise;
    if (res.success) {
      MySwal.fire({ toast: true, position: "top-end", icon: "success", title: okText, showConfirmButton: false, timer: 2000 });
      load();
    } else {
      MySwal.fire({ icon: "error", title: "Error", text: res.message || "No se pudo completar la acción." });
    }
  };

  const assign = async (driver: IDeliveryDriver) => {
    if (!assigning) return;
    const target = assigning;
    setAssigning(null);
    await run(DeliveryBoardService.assign(target.id, driver.id), `#${target.venta_id} asignado a ${driver.name}`);
  };

  const manualDeliver = async (d: IDeliveryBoardItem) => {
    const confirm = await MySwal.fire({
      title: `¿Marcar #${d.venta_id} como entregado?`,
      text: "Queda registrado como entrega manual, sin foto ni código del cliente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, entregado",
      cancelButtonText: "Cancelar",
    });
    if (confirm.isConfirmed) await run(DeliveryBoardService.manualDeliver(d.id), `#${d.venta_id} entregado`);
  };

  const resolve = async (d: IDeliveryBoardItem, action: "retry" | "pool" | "cancel") => {
    const texts = {
      retry: { title: "Reintentar la entrega", text: `${d.driver?.name || "El repartidor"} vuelve a intentarlo.` },
      pool: { title: "Devolver a la bolsa", text: "El repartidor regresa el pedido a la sucursal y cualquier repartidor lo podrá tomar." },
      cancel: { title: "Cancelar la entrega", text: "La entrega se cancela. Si también hay que cancelar la venta, hazlo desde Cancelar." },
    }[action];
    const confirm = await MySwal.fire({ ...texts, icon: "question", showCancelButton: true, confirmButtonText: "Confirmar", cancelButtonText: "Volver" });
    if (confirm.isConfirmed) await run(DeliveryBoardService.resolve(d.id, action), "Incidencia resuelta");
  };

  const photo = async (eventId: number) => {
    const res = await DeliveryBoardService.photoUrl(eventId);
    if (res.success && res.object?.url) window.open(res.object.url, "_blank");
    else MySwal.fire({ icon: "error", title: "Error", text: res.message || "No se pudo abrir la foto." });
  };

  const card = (d: IDeliveryBoardItem) => (
    <DeliveryCard
      key={d.id}
      delivery={d}
      selected={d.id === selectedId}
      onSelect={() => setSelectedId(d.id)}
      onAssign={() => setAssigning(d)}
      onManualDeliver={() => manualDeliver(d)}
      onResolve={(action) => resolve(d, action)}
      onPhoto={photo}
    />
  );

  if (!warehouseId) return <Alert severity="warning">Abre la caja para ver los domicilios de tu sucursal.</Alert>;

  const drivers = board?.drivers || [];
  const cashToday = closed.reduce((sum, d) => sum + (d.cash_collected || 0), 0);

  return (
    <Box sx={{ height: "calc(90vh - 90px)", display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="open">Abiertos ({open.length})</ToggleButton>
          <ToggleButton value="closed">Cerrados hoy ({closed.length})</ToggleButton>
          <ToggleButton value="settlement">Liquidación</ToggleButton>
        </ToggleButtonGroup>
        <Typography sx={{ fontSize: 12, color: "#aaa", flex: 1 }}>
          {updatedAt ? `Actualizado ${updatedAt}` : ""} {loading ? "· actualizando…" : ""}
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Actualizar</Button>
      </Box>

      {!!error && <Alert severity="error">{error}</Alert>}

      {view === "settlement" ? (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DeliverySettlement warehouseId={warehouseId} cashBoxId={user?.cbox?.id} />
        </Box>
      ) : (
      <Grid container spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
        <Grid item xs={8} sx={{ height: "100%" }}>
          {view === "open" ? (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1, height: "100%" }}>
              {COLUMNS.map((col) => {
                const items = open.filter((d) => col.statuses.includes(d.status));
                return (
                  <Paper key={col.key} sx={{ bgcolor: "#222", p: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                    <Typography sx={{ fontWeight: 800, mb: 1, color: col.key === "failed" && items.length ? "#ff8a80" : "#F5E03B" }}>
                      {col.title} · {items.length}
                    </Typography>
                    <Box sx={{ overflowY: "auto", flex: 1 }}>
                      {items.length === 0 ? <Typography sx={{ fontSize: 12, color: "#777" }}>Sin pedidos</Typography> : items.map(card)}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          ) : (
            <Paper sx={{ bgcolor: "#222", p: 1, height: "100%", overflowY: "auto" }}>
              <Typography sx={{ fontWeight: 800, mb: 1, color: "#F5E03B" }}>
                Entregados y cancelados hoy · efectivo cobrado por repartidores {money(cashToday)}
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1 }}>
                {closed.map(card)}
              </Box>
            </Paper>
          )}
        </Grid>

        <Grid item xs={4} sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ flex: 1, minHeight: 240 }}>
            <DeliveryMap deliveries={open} drivers={drivers} selectedId={selectedId} onSelect={setSelectedId} />
          </Box>
          <Paper sx={{ bgcolor: "#222", p: 1, maxHeight: "40%", overflowY: "auto" }}>
            <Typography sx={{ fontWeight: 800, color: "#F5E03B", mb: 0.5 }}>Repartidores</Typography>
            {drivers.length === 0 && <Typography sx={{ fontSize: 12, color: "#777" }}>No hay repartidores dados de alta en esta sucursal.</Typography>}
            {drivers.map((d) => (
              <Box key={d.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, borderBottom: "1px solid #333" }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: d.on_shift ? "#239e2e" : "#666" }} />
                <Typography sx={{ fontSize: 13, flex: 1 }}>{d.name}</Typography>
                <Typography sx={{ fontSize: 12, color: "#aaa" }}>
                  {d.on_shift ? `${d.in_route} en ruta · ${d.assigned} asignados` : "fuera de turno"}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>
      )}

      <Dialog open={!!assigning} onClose={() => setAssigning(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Asignar #{assigning?.venta_id}</DialogTitle>
        <DialogContent>
          {drivers.length === 0 && <Typography>No hay repartidores en esta sucursal.</Typography>}
          <List>
            {drivers.map((d) => (
              <ListItemButton key={d.id} onClick={() => assign(d)} disabled={d.id === assigning?.driver?.id}>
                <ListItemText
                  primary={`${d.name}${d.on_shift ? "" : " (fuera de turno)"}`}
                  secondary={`${d.in_route} en ruta · ${d.assigned} asignados`}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
